import CollectSDK, { type CollectCustomProperties, type CollectSDKInitConfig } from '@wegic/collect';
import { getFirstVisitRefer, getFirstVisitURL } from '@wegic/collect/seo';
import { isLocal } from '@/const/env';
import { useUserStore } from '@/stores/user';
import { getProjectIdFromUrl } from '../CustomRouter/handler';
import {
  resolveAnonymousCollectUserId,
  resolveInitialCollectUserId,
} from './collectUserId';
import { ACTION_TO_THEME_MAP, BUSINESS_TYPE, COLLECT_SERVER_URL, type CollectEventName, EVENT_PREFIX } from './const';

/** 初始化埋点 SDK */
export function initCollectSDK(
  userId: string | null,
  isOnline: boolean,
  customConfig?: Partial<CollectSDKInitConfig>,
) {
  const { isLogin, userInfo } = useUserStore.getState();
  const collectUserId = resolveInitialCollectUserId(userId ?? userInfo?.userId);
  const config: CollectSDKInitConfig = {
    userId: collectUserId,
    isAnonymous: !isLogin,
    isOnline,
    businessType: BUSINESS_TYPE,
    actionToThemeMap: ACTION_TO_THEME_MAP,
    serverUrl: COLLECT_SERVER_URL,
    eventPrefix: EVENT_PREFIX,
    enableBatch: true,
    batchSize: 5,
    batchSendInterval: 3000,
    onError: (error: unknown, message?: string) => {
      if (isOnline) {
        console.error('[CollectSDK]', message, error);
      } else {
        console.warn('[CollectSDK]', message, error);
      }
    },
    ...customConfig,
  };

  CollectSDK.init(config);
}

/** 获取通用埋点属性 */
export function getCommonCollectProperties() {
  return {
    project_id: getProjectIdFromUrl(),
    firstEnterUrl: getFirstVisitURL(),
    referrerUrl: getFirstVisitRefer(),
    version: isLocal ? undefined : (typeof window !== 'undefined' ? window.scm?.gitHash : undefined),
  };
}

/** 发送埋点事件 */
export function collect(eventName: CollectEventName, customProperties: CollectCustomProperties = {}) {
  const commonProperties = getCommonCollectProperties();

  // 本地开发环境检查是否启用埋点上报
  if (isLocal) {
    const debugSettings = localStorage.getItem('debug-settings');
    if (debugSettings) {
      try {
        const settings = JSON.parse(debugSettings);
        if (!settings?.state?.enableLocalCollect) {
          return;
        }
      } catch {
        return;
      }
    } else {
      return;
    }
  }

  CollectSDK.collect(eventName, {
    ...commonProperties,
    ...customProperties,
  });
}

/** 手动刷新埋点队列，在需要立即发送埋点数据时调用(如页面卸载前) */
export function flushCollectQueue() {
  CollectSDK.flush();
}

/** 设置用户ID，在用户登录或切换账号后调用 */
export function setCollectUserId(userId: string | null) {
  CollectSDK.setConfig('isAnonymous', !userId);
  CollectSDK.setConfig('userId', userId);
}

/** 同步匿名用户ID，仅在未登录匿名态初始化后调用 */
export function setAnonymousCollectUserId(anonymousUserId?: string | null) {
  CollectSDK.setConfig('isAnonymous', true);
  CollectSDK.setConfig('userId', resolveAnonymousCollectUserId(anonymousUserId));
}
